import { getApiConfig } from "@/config/api.config";
import { BaseApiService } from "@/services/api/BaseApiService";
import { CoreService } from "@/services/core/CoreService";

export interface RFIDReader {
  name: string;
  storeId: number;
  ports: any[];
  debug: boolean;
  username?: string;
  password?: string;
  enableDeduplication: boolean;
  externalId: string;
}

export interface RFIDReaderResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface CreateReaderRequest {
  storeId: number;
  name: string;
  username: string;
  password: string;
  externalId: string;
  debug: boolean;
  ports: Array<{
    port: number;
    changingRoomId: number;
    rssiFilter: number;
  }>;
  enableDeduplication: boolean;
}

export interface UpdateReaderRequest {
  storeId: number;
  name: string;
  username: string;
  password: string;
  externalId: string;
  debug: boolean;
  ports: Array<{
    port: number;
    changingRoomId: number;
    rssiFilter: number;
  }>;
  enableDeduplication: boolean;
}

export class RFIDReaderService extends BaseApiService {
  private static instance: RFIDReaderService;
  protected readonly coreService: CoreService;
  private readonly authorization: string;

  private constructor() {
    super();
    this.coreService = CoreService.getInstance();
    const user = this.coreService.getStoredUser();
    this.authorization = user?.token || "";
  }

  public static getInstance(): RFIDReaderService {
    if (!RFIDReaderService.instance) {
      RFIDReaderService.instance = new RFIDReaderService();
    }
    return RFIDReaderService.instance;
  }

  private getCoreBaseUrl(): string {
    return getApiConfig().core;
  }

  public async getAllReaders(
    customerId?: number,
    storeId?: number,
  ): Promise<RFIDReaderResponse<RFIDReader[]>> {
    try {
      const params = new URLSearchParams();
      if (customerId) params.append("customerId", customerId.toString());
      if (storeId) params.append("storeId", storeId.toString());

      const response = await this.get<{
        data: RFIDReader[];
      }>(`/rfidReader${params.toString() ? "?" + params.toString() : ""}`, this.getCoreBaseUrl());
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to fetch RFID readers",
      };
    }
  }

  public async getReaderByName(readerName: string): Promise<RFIDReaderResponse<RFIDReader>> {
    try {
      const response = await this.get<{ data: RFIDReader }>(
        `/rfidReader/${readerName}`,
        this.getCoreBaseUrl(),
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to fetch RFID reader",
      };
    }
  }

  public async createReader(reader: CreateReaderRequest): Promise<RFIDReaderResponse<RFIDReader>> {
    try {
      const response = await this.post<{ data: RFIDReader }>(
        "/rfidReader",
        reader,
        this.getCoreBaseUrl(),
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to create RFID reader",
      };
    }
  }

  public async updateReader(reader: UpdateReaderRequest): Promise<RFIDReaderResponse<RFIDReader>> {
    try {
      const response = await this.put<{ data: RFIDReader }>(
        "/rfidReader",
        reader,
        this.getCoreBaseUrl(),
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to update RFID reader",
      };
    }
  }

  public async resetDeduplication(readerName: string): Promise<RFIDReaderResponse<void>> {
    try {
      await this.delete(`/rfidReader/${readerName}/previousShopperItem`, this.getCoreBaseUrl());
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to reset deduplication",
      };
    }
  }
}
